/**
 * Story moderation writes: approve, reject (reason required), Featured rank (editorial, Admin only) and
 * hide/unhide (reason required).
 * Mount each only while open, so every open starts clean.
 */

import { useState } from 'react'
import { Alert, Button, TextField, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { storiesApi } from '@api/endpoints/stories'
import type { AdminStoryRowDto } from '@api/types/stories'
import { apiErrorMessage } from '@features/admin/components/users/userFormatting'
import { UserDataActionDialog } from '@features/admin/components/users/UserDataActionDialog'
import { STORIES_KEY } from '../storyQueryKeys'

export function FeaturedRankDialog({ story, onClose }: { story: AdminStoryRowDto; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(story.featuredRank != null ? String(story.featuredRank) : '')
  const [problem, setProblem] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (rank: number | null) => storiesApi.setFeaturedRank(story.id, rank),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORIES_KEY })
      onClose()
    },
  })

  const save = () => {
    const trimmed = value.trim()
    if (trimmed === '') {
      mutation.mutate(null)
      return
    }
    const rank = Number(trimmed)
    if (!Number.isInteger(rank) || rank < 1 || rank > 999) {
      setProblem('Enter a whole number from 1 to 999, or leave it empty to clear.')
      return
    }
    setProblem(null)
    mutation.mutate(rank)
  }

  const error = problem ?? (mutation.error ? apiErrorMessage(mutation.error, 'The rank could not be saved.') : null)

  return (
    <ConfirmDialog
      open
      title={`Featured rank: ${story.name}`}
      message="Lower shows first on the Stories shelf; empty means not featured."
      confirmLabel={value.trim() === '' ? 'Clear rank' : 'Save rank'}
      loading={mutation.isPending}
      onConfirm={save}
      onClose={onClose}
    >
      <Alert severity="info" sx={{ mt: 2 }}>
        Featuring is editorial curation by staff. Never set or change a rank because a business, sponsor or
        creator asked or paid for it.
      </Alert>
      {!story.isPublic && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This Story is hidden. Its rank has no effect until it is public again.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <TextField
        label="Featured rank"
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        fullWidth
        autoFocus
        inputProps={{ min: 1, max: 999, step: 1 }}
        sx={{ mt: 2 }}
      />
      {story.featuredRank != null && (
        <Button size="small" sx={{ mt: 1 }} onClick={() => setValue('')}>
          Clear (not featured)
        </Button>
      )}
    </ConfirmDialog>
  )
}

export function StoryVisibilityDialog({ story, onClose }: { story: AdminStoryRowDto; onClose: () => void }) {
  const queryClient = useQueryClient()
  const hiding = story.isPublic

  const mutation = useMutation({
    mutationFn: (reason: string) => storiesApi.setVisibility(story.id, !hiding, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORIES_KEY })
      onClose()
    },
  })

  return (
    <UserDataActionDialog
      title={hiding ? `Hide ${story.name}?` : `Unhide ${story.name}?`}
      message={
        hiding
          ? 'The Story leaves the Stories shelf, its page and its reviews for everyone except staff and its managers. Its stops are not changed.'
          : 'The Story returns to the Stories shelf and its page and reviews become public again.'
      }
      confirmLabel={hiding ? 'Hide story' : 'Unhide story'}
      destructive={hiding}
      pending={mutation.isPending}
      error={mutation.error ? apiErrorMessage(mutation.error, 'The change could not be made.') : null}
      onSubmit={(reason) => mutation.mutate(reason)}
      onClose={onClose}
    >
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
        The reason is recorded on the Story owner's audit history.
      </Typography>
    </UserDataActionDialog>
  )
}

/** Approve a Story: it is listed while public and active, and its editors get a notice. */
export function StoryApproveDialog({ story, onClose }: { story: AdminStoryRowDto; onClose: () => void }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => storiesApi.approve(story.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORIES_KEY })
      onClose()
    },
  })

  return (
    <ConfirmDialog
      open
      title={`Approve ${story.name}?`}
      message={
        story.isPublic
          ? 'The Story goes live: the Stories shelf, its page and its reviews. Its editors are told.'
          : 'The Story is approved but hidden; it goes live when it is made public. Its editors are told.'
      }
      confirmLabel="Approve"
      loading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
      onClose={onClose}
    >
      {story.storyReview === 3 && story.storyReviewNote && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Rejected earlier: {story.storyReviewNote}
        </Alert>
      )}
      {(story.hasAccessCriteria || story.gatedChapterCount > 0) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Some of this Story is gated by access criteria. Check the access before approving.
        </Alert>
      )}
      {mutation.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiErrorMessage(mutation.error, 'The Story could not be approved.')}
        </Alert>
      )}
    </ConfirmDialog>
  )
}

/** Reject a Story with a reason (required). The reason is shown to its editors. */
export function StoryRejectDialog({ story, onClose }: { story: AdminStoryRowDto; onClose: () => void }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (reason: string) => storiesApi.reject(story.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORIES_KEY })
      onClose()
    },
  })

  return (
    <UserDataActionDialog
      title={`Reject ${story.name}?`}
      message={
        story.storyReview === 2
          ? 'This Story is approved now. Rejecting it takes it off the Stories shelf until it is approved again.'
          : 'The Story stays unlisted. Its editors see your reason and can submit it again.'
      }
      confirmLabel="Reject story"
      destructive
      pending={mutation.isPending}
      error={mutation.error ? apiErrorMessage(mutation.error, 'The Story could not be rejected.') : null}
      onSubmit={(reason) => mutation.mutate(reason)}
      onClose={onClose}
    >
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
        Write the reason for the Story's editors: what to change before it can go live. It is also recorded on the
        owner's audit history.
      </Typography>
    </UserDataActionDialog>
  )
}
